import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import PDFThumbnail from '@/components/PDFThumbnail';
import AdSpace from '@/components/AdSpace';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Newspaper, Calendar, Eye, Phone, MapPin, Info, ChevronRight, Share2, HeartPulse } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [latestEdition, setLatestEdition] = useState(null);
  const [pastEditions, setPastEditions] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [editionsRes, adsRes] = await Promise.all([
          axios.get(`${API}/editions`),
          axios.get(`${API}/ads`)
        ]);
        const sorted = editionsRes.data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setLatestEdition(sorted[0]);
        setPastEditions(sorted.slice(1, 9)); 
        setAds(adsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getAdByType = (type) => ads.find(ad => ad.type === type);

  if (loading) return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <div className="flex-1 flex justify-center items-center">
        <div className="animate-pulse text-[#EB1B1B] font-bold font-kannada text-xl">ಲೋಡ್ ಆಗುತ್ತಿದೆ...</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      {/* BRANDING HEADER */}
      <div className="bg-white border-b border-gray-100 pt-8 pb-4 text-center">
        <h1 className="text-5xl md:text-7xl font-black text-[#EB1B1B]" style={{ fontFamily: 'Noto Serif Kannada, serif' }}>
          ಕಲ್ಲಿನ ಕೂಗು
        </h1>
        <p className="text-gray-400 font-bold text-[10px] tracking-[0.3em] uppercase mt-2">ಕನ್ನಡ ದಿನಪತ್ರಿಕೆ - ಚಿತ್ರದುರ್ಗ ಜಿಲ್ಲೆ</p>
      </div>

      {/* RESPONSIVE LEADERBOARD AD - Responsive for PC/Mobile */}
      <div className="bg-white py-4 md:py-8 border-b border-gray-50">
        <div className="container mx-auto px-4 flex flex-col items-center">
          <div className="w-full bg-gray-50 border border-dashed border-gray-200 rounded-lg flex items-center justify-center overflow-hidden min-h-[60px] md:min-h-[120px]">
             <AdSpace type="leaderboard" ad={getAdByType('leaderboard')} />
          </div>
          <p className="text-[8px] text-gray-300 mt-1 uppercase tracking-tighter text-center">Advertisement</p>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          <div className="lg:col-span-9 space-y-12">
            
            {/* MAIN FEATURE: TODAY'S NEWSPAPER */}
            {latestEdition && (
              <section className="bg-white rounded-[32px] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden flex flex-col md:row">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  {/* Left: Preview */}
<div className="bg-[#fdfdfd] flex items-center justify-center p-8 border-r border-gray-50">
  {/* We changed the 'div' to 'Link' and added 'to' and 'cursor-pointer' */}
  <Link 
    to={`/viewer/${latestEdition.id}`} 
    className="w-full max-w-[320px] shadow-[0_20px_50px_rgba(0,0,0,0.15)] rounded-sm overflow-hidden transition-all hover:scale-[1.05] hover:shadow-[0_30px_60px_rgba(0,0,0,0.25)] block cursor-pointer group relative"
  >
    <PDFThumbnail pdfUrl={latestEdition.pdfUrl} />
    
    {/* Subtle hover overlay to guide the reader */}
    <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
       <div className="bg-white/95 text-black text-[10px] font-black px-4 py-2 rounded-full shadow-xl transform translate-y-2 group-hover:translate-y-0 transition-transform font-kannada">
         ಓದಲು ಕ್ಲಿಕ್ ಮಾಡಿ
       </div>
    </div>
  </Link>
</div>

                  {/* Right: Info Section */}
                  <div className="p-8 md:p-12 flex flex-col justify-between relative overflow-hidden bg-white">
                    <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-red-50 rounded-full blur-3xl opacity-50"></div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="bg-red-100 text-[#EB1B1B] px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Today's Edition</span>
                        <span className="text-gray-400 font-bold text-xs flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {new Date(latestEdition.date).toLocaleDateString('kn-IN')}
                        </span>
                      </div>
                      
                      <h3 className="text-3xl md:text-5xl font-black text-gray-900 leading-tight mb-6" style={{ fontFamily: 'Noto Serif Kannada, serif' }}>
                        {latestEdition.title}
                      </h3>

                      {/* ABOUT KALLINA KOOGU: Fills space above button */}
                      <div className="py-4 border-t border-b border-gray-50 my-6">
                        <p className="text-[10px] font-black text-[#EB1B1B] uppercase tracking-widest mb-2">ನಮ್ಮ ಬಗ್ಗೆ</p>
                        <p className="text-sm font-bold text-gray-600 leading-relaxed font-kannada">
                          ಕಲ್ಲಿನ ಕೂಗು ಪತ್ರಿಕೆಯು 2016 ರಿಂದ ಚಿತ್ರದುರ್ಗ ಜಿಲ್ಲೆಯ ಜನಪ್ರಿಯ ಪ್ರಾದೇಶಿಕ ದಿನಪತ್ರಿಕೆಯಾಗಿ ಹೊರಹೊಮ್ಮಿದೆ. 
                          ನಿಖರ ಸುದ್ದಿ, ಪಾರದರ್ಶಕ ವರದಿಗಾರಿಕೆ ಮತ್ತು ಸಾರ್ವಜನಿಕರ ಧ್ವನಿಯಾಗಿ ನಾವು ನಿರಂತರವಾಗಿ ಶ್ರಮಿಸುತ್ತಿದ್ದೇವೆ. 
                          ನಮ್ಮ ಡಿಜಿಟಲ್ ಆವೃತ್ತಿಯ ಮೂಲಕ ಪ್ರಪಂಚದ ಯಾವುದೇ ಮೂಲೆಯಿಂದ ಇಂದಿನ ಪತ್ರಿಕೆಯನ್ನು ಉಚಿತವಾಗಿ ಓದಿ.
                        </p>
                      </div>

                      <div className="flex items-center gap-6 text-gray-400 text-sm font-bold mb-8">
                        <span className="flex items-center gap-1.5"><Eye className="h-4 w-4 text-red-500" /> {latestEdition.views} ವೀಕ್ಷಣೆಗಳು</span>
                        <button 
                          onClick={() => window.open(`https://wa.me/?text=ಇಂದಿನ ಕಲ್ಲಿನ ಕೂಗು ಪತ್ರಿಕೆ ಓದಿ: ${window.location.href}`)}
                          className="flex items-center gap-1.5 hover:text-green-600 transition-colors"
                        >
                          <Share2 className="h-4 w-4" /> ಹಂಚಿಕೊಳ್ಳಿ
                        </button>
                      </div>
                    </div>

                    <Link to={`/viewer/${latestEdition.id}`} className="relative z-10">
                      <Button className="w-full bg-[#EB1B1B] hover:bg-red-700 text-white font-black text-xl py-10 rounded-2xl shadow-2xl shadow-red-200 transition-all active:scale-[0.98] flex items-center justify-center gap-4">
                        <Newspaper className="h-7 w-7" /> {t('readNow')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </section>
            )}

            {/* DISTRICT INFORMATION HUB */}
            <section className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="bg-gray-900 px-8 py-5 flex items-center justify-between">
                <h3 className="text-white font-bold font-kannada text-xl flex items-center gap-3">
                  <Info className="h-5 w-5 text-red-500" /> ಚಿತ್ರದುರ್ಗ ಜಿಲ್ಲಾ ಮಾಹಿತಿ ಕೇಂದ್ರ
                </h3>
              </div>
              
              <div className="p-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                <div className="space-y-4">
                  <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Administration</h4>
                  <div className="space-y-4">
                    <div><p className="text-[10px] text-gray-400 font-bold uppercase">DC Office</p><p className="font-bold text-gray-800 text-sm">08194 - 222538, 222811</p></div>
                    <div><p className="text-[10px] text-gray-400 font-bold uppercase">SP Office</p><p className="font-bold text-gray-800 text-sm">08194 - 222781 | 9480803101</p></div>
                    <div><p className="text-[10px] text-gray-400 font-bold uppercase">Zilla Panchayath</p><p className="font-bold text-gray-800 text-sm">08194 - 22306</p></div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Emergency</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-red-50 p-3 rounded-xl border border-red-100">
                      <p className="text-[9px] text-red-600 font-black uppercase">Dist Hospital</p>
                      <p className="text-sm font-black text-gray-800">08194 - 234710</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-xl"><p className="text-[9px] text-gray-400 font-black uppercase">Police</p><p className="text-sm font-black text-gray-800">112</p></div>
                    <div className="bg-gray-50 p-3 rounded-xl"><p className="text-[9px] text-gray-400 font-black uppercase">Ambulance</p><p className="text-sm font-black text-gray-800">108</p></div>
                    <div className="bg-gray-50 p-3 rounded-xl"><p className="text-[9px] text-gray-400 font-black uppercase">Fire</p><p className="text-sm font-black text-gray-800">101</p></div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">District Taluks</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['ಚಿತ್ರದುರ್ಗ', 'ಚಳ್ಳಕೆರೆ', 'ಹಿರಿಯೂರು', 'ಹೊಳಲ್ಕೆರೆ', 'ಹೊಸದುರ್ಗ', 'ಮೊಳಕಾಲ್ಮೂರು'].map(t => (
                      <div key={t} className="text-[11px] font-bold text-gray-700 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 text-center uppercase">
                        {t}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* PREVIOUS EDITIONS GRID */}
            <section>
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-black text-gray-900 font-kannada">ಹಿಂದಿನ ಸಂಚಿಕೆಗಳು (Previous Editions)</h3>
                <Link to="/archive" className="flex items-center gap-1 text-xs font-black text-[#EB1B1B] hover:underline">
                  VIEW ARCHIVE <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {pastEditions.map((edition) => (
                  <Link key={edition.id} to={`/viewer/${edition.id}`} className="group bg-white p-3 rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-xl hover:-translate-y-2">
                    <div className="aspect-[3/4] bg-gray-50 rounded-xl mb-3 overflow-hidden border border-gray-50">
                      <PDFThumbnail pdfUrl={edition.pdfUrl} />
                    </div>
                    <p className="text-[10px] font-black text-gray-400 text-center uppercase tracking-widest">
                      {new Date(edition.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          {/* SIDEBAR: SEARCH AND 3 IDENTICAL VERTICAL ADS */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">ಪತ್ರಿಕೆ ಹುಡುಕಿ (Search)</h4>
              <input type="date" className="w-full mb-4 p-3 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold outline-none focus:border-red-200" />
              <Button onClick={() => navigate('/archive')} className="w-full bg-gray-900 hover:bg-black text-white text-[11px] font-bold h-11 rounded-xl">SEARCH EDITION</Button>
            </div>
            
            <div className="sticky top-24 space-y-6">
              {/* Ad Slot 1 */}
              <div className="w-full aspect-square bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden p-3 flex items-center justify-center">
                <div className="w-full h-full bg-gray-50 rounded-2xl overflow-hidden flex items-center justify-center">
                  <AdSpace type="sidebar" ad={getAdByType('sidebar')} />
                </div>
              </div>
              
              {/* Ad Slot 2 (Now matches Slot 1) */}
              <div className="w-full aspect-square bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden p-3 flex items-center justify-center">
                <div className="w-full h-full bg-gray-50 rounded-2xl overflow-hidden flex items-center justify-center">
                  <AdSpace type="sidebar_v2" ad={getAdByType('sidebar_v2')} />
                </div>
              </div>

              {/* Ad Slot 3 (Now matches Slot 1) */}
              <div className="w-full aspect-square bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden p-3 flex items-center justify-center">
                <div className="w-full h-full bg-gray-50 rounded-2xl overflow-hidden flex items-center justify-center">
                  <AdSpace type="interstitial" ad={getAdByType('interstitial')} />
                </div>
              </div>

              <p className="text-[9px] text-center text-gray-300 uppercase tracking-widest font-bold">ಜಾಹೀರಾತು (Ads)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
